import "@/App.css";
import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, NavLink, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import { Database, FileSpreadsheet, Trees, LogOut, Loader2, Users, Tags, KanbanSquare, Bell } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { API, notificationsApi } from "@/lib/api";
import MaterialLibrary from "@/pages/MaterialLibrary";
import ProjectList from "@/pages/ProjectList";
import ProjectEditor from "@/pages/ProjectEditor";
import UsersAdmin from "@/pages/UsersAdmin";
import SalesPriceList from "@/pages/SalesPriceList";
import TaskBoard from "@/pages/TaskBoard";
import Login from "@/pages/Login";

function NotificationBell() {
  const nav = useNavigate();
  const [data, setData] = useState({ unread: 0, items: [] });
  const [open, setOpen] = useState(false);
  const load = () => notificationsApi.list().then(setData).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  const onClick = async (n) => {
    if (!n.read) { await notificationsApi.read(n.id).catch(() => {}); load(); }
    setOpen(false);
    if (n.task_id) nav("/tasks");
  };
  const markAll = async () => { await notificationsApi.readAll().catch(() => {}); load(); };
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} data-testid="notif-bell"
        className="relative p-2 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900">
        <Bell className="w-5 h-5" />
        {data.unread > 0 && (
          <span data-testid="notif-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg z-50" data-testid="notif-dropdown">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
            <span className="text-sm font-bold text-slate-900">Bildirimler</span>
            {data.unread > 0 && <button onClick={markAll} data-testid="notif-mark-all" className="text-xs text-amber-600 hover:underline">Tümünü okundu yap</button>}
          </div>
          {data.items.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-slate-400">Bildirim yok</p>
          ) : data.items.map((n) => (
            <button key={n.id} onClick={() => onClick(n)} data-testid={`notif-item-${n.id}`}
              className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 ${n.read ? "opacity-60" : ""}`}>
              <div className="flex items-center gap-2">
                {!n.read && <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />}
                <span className="text-sm font-semibold text-slate-800">{n.title}</span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.body}</p>
              <p className="text-[10px] text-slate-400 mt-1">{(n.created_at || "").slice(0, 16).replace("T", " ")}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Sidebar() {
  const { user, logout } = useAuth();
  const link = (active) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
      active ? "bg-amber-600 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
    }`;
  return (
    <aside className="w-full md:w-64 md:min-h-screen bg-white border-b md:border-b-0 md:border-r border-slate-200 flex md:flex-col shrink-0">
      <div className="flex items-center gap-2 px-6 py-5 border-r md:border-r-0 md:border-b border-slate-200">
        <img src="https://customer-assets-v7afamib.emergentagent.net/job_timber-calc-14/artifacts/vb5x6rvd_Resim8.jpg" alt="NasWood" className="h-9 w-auto object-contain shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-heading font-extrabold text-slate-900 leading-none tracking-tight">
            Nas-TimberCalc
          </div>
          <div className="text-xs text-slate-400">Quoting suite</div>
        </div>
        {user && <NotificationBell />}
      </div>
      <nav className="flex md:flex-col gap-1 p-3 md:p-4 flex-1">
        <NavLink to="/" end className={({ isActive }) => link(isActive)} data-testid="nav-projects">
          <FileSpreadsheet className="w-5 h-5" /> Projects
        </NavLink>
        <NavLink to="/materials" className={({ isActive }) => link(isActive)} data-testid="nav-materials">
          <Database className="w-5 h-5" /> Material Library
        </NavLink>
        <NavLink to="/tasks" className={({ isActive }) => link(isActive)} data-testid="nav-tasks">
          <KanbanSquare className="w-5 h-5" /> Görevler
        </NavLink>
        <NavLink to="/sales-prices" className={({ isActive }) => link(isActive)} data-testid="nav-sales-prices">
          <Tags className="w-5 h-5" /> Satış Fiyat Listesi
        </NavLink>
        {user?.is_admin && (
          <NavLink to="/users" className={({ isActive }) => link(isActive)} data-testid="nav-users">
            <Users className="w-5 h-5" /> Kullanıcılar
          </NavLink>
        )}
      </nav>
      {user && (
        <div className="hidden md:flex items-center gap-3 p-4 border-t border-slate-200">
          {user.picture ? (
            <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-600">
              {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-slate-900 truncate" data-testid="user-name">{user.name || "User"}</div>
            <div className="text-xs text-slate-400 truncate">{user.email}</div>
          </div>
          <button onClick={logout} data-testid="logout-btn" title="Sign out"
            className="p-2 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-900 transition-colors">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      )}
    </aside>
  );
}

function Shell({ children }) {
  return (
    <div className="flex min-h-screen bg-slate-100 flex-col md:flex-row">
      <Sidebar />
      <main className="flex-1 min-w-0">{children}</main>
    </div>
  );
}

function FullLoader({ label = "Loading…" }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-100 gap-3">
      <Loader2 className="w-7 h-7 text-amber-600 animate-spin" />
      <p className="text-sm text-slate-500">{label}</p>
    </div>
  );
}

function AuthCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const params = new URLSearchParams(window.location.hash.replace("#", ""));
    const sid = params.get("session_id");
    (async () => {
      try {
        const r = await axios.post(`${API}/auth/session`, { session_id: sid }, { withCredentials: true });
        setUser(r.data);
      } catch (e) {
        const msg = e?.response?.data?.detail;
        if (e?.response?.status === 403 && msg) {
          try { sessionStorage.setItem("auth_error", msg); } catch (_) {}
        }
      }
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
      navigate("/", { replace: true });
    })();
  }, [setUser, navigate]);
  return <FullLoader label="Signing you in…" />;
}

function Gate() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const hash = location.hash || window.location.hash || "";
  if (hash.includes("session_id=")) return <AuthCallback />;
  if (loading) return <FullLoader />;
  if (!user) return <Login />;
  return (
    <Shell>
      <Routes>
        <Route path="/" element={<ProjectList />} />
        <Route path="/materials" element={<MaterialLibrary />} />
        <Route path="/tasks" element={<TaskBoard />} />
        <Route path="/sales-prices" element={<SalesPriceList />} />
        <Route path="/users" element={<UsersAdmin />} />
        <Route path="/projects/:id" element={<ProjectEditor />} />
      </Routes>
    </Shell>
  );
}

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <AuthProvider>
        <BrowserRouter>
          <Gate />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
